import { useEffect, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import VersionFormModal from "../components/VersionFormModal";

export default function SWVersions({ user }) {
  const [versions, setVersions] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);

  const isPm = Boolean(user?.isAdmin);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      const res = await fetchWithAuth("/api/versions");

      if (!res.ok) {
        console.error("Failed to load versions");
        return;
      }

      const data = await res.json();
      setVersions(data);
    } catch (err) {
      console.warn("loadVersions aborted:", err.message);
    }
  };

  const deleteVersion = async (id) => {
    if (!window.confirm("Delete this version?")) return;

    const res = await fetchWithAuth(`/api/versions/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Delete failed");
      return;
    }

    loadVersions();
  };

  const visibleVersions = versions
    .filter((version) => showClosed || !version.closed)
    .sort((leftVersion, rightVersion) => {
      if (showClosed) {
        const closedCompare = Number(Boolean(rightVersion.closed)) - Number(Boolean(leftVersion.closed));
        if (closedCompare !== 0) return closedCompare;
      }

      const activeCompare = Number(Boolean(rightVersion.isActive)) - Number(Boolean(leftVersion.isActive));
      if (activeCompare !== 0) return activeCompare;

      return String(leftVersion.name ?? "").localeCompare(
        String(rightVersion.name ?? ""),
        undefined,
        { sensitivity: "base", numeric: true }
      );
    });

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (version, index) => {
    if (version.isActive) {
      return {
        "--bs-table-bg": "rgba(255, 193, 7, 0.18)"
      };
    }

    if (version.closed) {
      return {
        "--bs-table-bg": "rgba(108, 117, 125, 0.12)"
      };
    }

    return {
      "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
    };
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div className="form-check m-0">
          <input
            id="show-closed-versions"
            type="checkbox"
            className="form-check-input"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="show-closed-versions">
            Show closed
          </label>
        </div>

        {isPm && (
          <button
            className="btn btn-success"
            onClick={() => {
              setEditingVersion(null);
              setShowForm(true);
            }}
          >
            Add version
          </button>
        )}
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ ...stickyHeaderStyle, width: "220px" }}>Name</th>
              <th style={stickyHeaderStyle}>Description</th>
              <th style={{ ...stickyHeaderStyle, width: "140px" }}>Added by</th>
              <th className="text-center" style={{ ...stickyHeaderStyle, width: "110px" }}>Active</th>
              <th className="text-center" style={{ ...stickyHeaderStyle, width: "110px" }}>Closed</th>
              {isPm && (
                <th style={{ ...stickyHeaderStyle, width: "120px" }}>Actions</th>
              )}
            </tr>
          </thead>

          <tbody>
            {visibleVersions.map((version, index) => (
              <tr key={version.id} style={getRowStyle(version, index)}>
                <td className="fw-semibold">
                  <span>{version.name}</span>
                  {version.isActive && (
                    <span className="badge bg-warning text-dark ms-2">ACTIVE</span>
                  )}
                </td>
                <td>{version.description || <span className="text-muted">-</span>}</td>
                <td>{version.User?.name ?? "-"}</td>
                <td className="text-center">
                  {version.isActive && (
                    <span className="badge bg-success">Yes</span>
                  )}
                </td>
                <td className="text-center">
                  {version.closed && (
                    <span className="badge bg-secondary">Closed</span>
                  )}
                </td>
                {isPm && (
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => {
                        setEditingVersion(version);
                        setShowForm(true);
                      }}
                    >
                      <FaEdit />
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteVersion(version.id)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <VersionFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadVersions}
        version={editingVersion}
      />
    </div>
  );
}
